import { useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

interface RequestCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  founderEmail: string;
  creditsUsed: number;
  creditsTotal: number;
}

const RequestCreditsDialog = ({
  open,
  onOpenChange,
  founderEmail,
  creditsUsed,
  creditsTotal,
}: RequestCreditsDialogProps) => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);

    // TODO: Replace with real API call (e.g. POST /credits/request) before production launch
    await new Promise((resolve) => setTimeout(resolve, 1000));

    toast.success("Credit request sent! We'll get back to you shortly.");

    setSending(false);
    setMessage("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[80%] max-w-lg rounded-2xl">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Request More Lead Credits
          </DialogTitle>
          <DialogDescription className="pt-2 text-base">
            You've used {creditsUsed} of {creditsTotal} leads. Send a request to
            the Stack team for additional credits.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-[#494949] mb-1.5 block">
              Why do you need more credits? (optional)
            </label>
            <Textarea
              placeholder="e.g., I'm expanding into a new market and need to find leads in the healthcare SaaS space..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[100px] resize-none"
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button
              variant="ghost"
              className="h-9 rounded-lg px-5 border border-[#e8e8e8] text-[#1e1e24] hover:bg-transparent"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-sds-purple-100 hover:bg-sds-purple-80 text-white rounded-lg px-5 h-9"
              onClick={handleSend}
              disabled={sending}
            >
              <Mail className="mr-2 h-4 w-4" />
              {sending ? "Sending..." : "Send Request"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestCreditsDialog;
